import {useMediaQueries} from 'presentation/pages';
import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {translations} from 'presentation/translations-new';
import {useEffect, useRef, useState} from 'react';
import {HEADER_TITLES} from '../../constants/headerTitles';
import {getOdontoStepPosition} from 'presentation/modules/booking/odonto/utils/getOdontoStepPosition';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {ODONTO_LABELS, ODONTO_LABELS_GEO} from '../../constants/stepLabels';
import {SEARCH_BAR_PLACEHOLDER} from '../../constants/searchBarPlaceholder';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {FeaturesEnum, HomeEnum, QueryParamsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useNavigate} from 'react-router-dom';
import {useBackButtonBooking} from 'presentation/modules/booking/shared/hooks/useBackButtonBooking';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';

const {bookOdonto} = translations['pt-br'].privateFlow.services;
const {searchSpecialty} = translations['pt-br'].privateFlow.bookingFlow;

export function useOdontoBooking() {
	const stepsVisited = useRef(new Set<number>());
	const [isFirstVisit, setIsFirstVisit] = useState(true);
	const {isMobile, isLaptop, isDesktop} = useMediaQueries();
	const hideSearchBar = useHideSearchBarStore(state => state.hideSearchBar);
	const setHideSearchBar = useHideSearchBarStore(state => state.setHideSearchBar);
	const hideStepBar = useHideStepBarStore(state => state.hideStepBar);
	const setHideStepBar = useHideStepBarStore(state => state.setHideStepBar);
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);
	const navigate = useNavigate();
	const {
		activeBookingFlowStep,
		bookingData,
		setActiveBookingFlowStep,
		setBookingData,
		setSearchParams,
		clearSearch,
	} = useQueryParamsSync({isFirstVisit});

	const {
		handleChangeModals,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
	} = useBookingSummary();

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		DISTRICT_STEP,
	} = getOdontoStepPosition({
		isManualLocation: bookingData.isManualLocation,
	});

	useBackButtonBooking({flowType: BookingTypeEnum.ODONTO});

	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const isUsingGeolocation = !bookingData.isManualLocation;

	const getHeaderTitle = () => {
		if (!isMobile) return bookOdonto;

		return isUsingGeolocation
			? HEADER_TITLES.withGeo[activeBookingFlowStep]
			: HEADER_TITLES.withoutGeo[activeBookingFlowStep];
	};

	const shouldHideBackBtn = bookingData.isRebooking && activeBookingFlowStep === DISTRICT_STEP;
	const stepLabels = isUsingGeolocation ? ODONTO_LABELS_GEO : ODONTO_LABELS;
	const searchBarPlaceholder = isUsingGeolocation
		? SEARCH_BAR_PLACEHOLDER.withGeo
		: SEARCH_BAR_PLACEHOLDER.withoutGeo;
	const isLastStep = activeBookingFlowStep === stepLabels.length - 1;

	const shouldHideSearchBarAndBookingSummary = activeBookingFlowStep === LOCATION_STEP;
	const shouldHideSearchBar = hideSearchBar || isLastStep || shouldHideSearchBarAndBookingSummary;

	const showSwipeableDrawerBookingSummary =
		!isLastStep && isLaptop && !shouldHideSearchBarAndBookingSummary;

	const shouldUseNewResumeLayout = isNewResumePageEnabled && isLastStep;

	const showOdontoSummarySectionGroupInGrid =
		isDesktop && !shouldHideSearchBarAndBookingSummary && !shouldUseNewResumeLayout;

	const shouldShowSpecialtyTagging =
		SEARCH_BAR_PLACEHOLDER[activeBookingFlowStep] === searchSpecialty;

	const searchBarTagId = shouldShowSpecialtyTagging
		? AppointmentBookingEnum.CLICK_BUSCAR_ESPECIALIDADE
		: undefined;

	const handleClose = () => {
		setAbandonFlowState(true);
	};

	const handleBack = () => {
		clearSearch();
		if (activeBookingFlowStep === LOCATION_STEP) {
			handleClose();
			return;
		}

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setBookingData(prev => ({
				...prev,
				isManualLocation: true,
			}));
		}
		if (activeBookingFlowStep === DISTRICT_STEP) {
			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				allDistrictsSelected: false,
			}));
		}
		if (activeBookingFlowStep === UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				odontoRecommendations: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.DISABILITY);
					prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);

					return prev;
				},
				{
					replace: true,
				},
			);
		}
		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitAddress: '',
				odontoUnitDistrictSelected: '',
				unitName: '',
				dateAt: '',
			}));
		}
		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);

					return prev;
				},
				{
					replace: true,
				},
			);
		}
		setActiveBookingFlowStep(value => value - 1);
	};

	const handleConfirmExitFlow = () => {
		setAbandonFlowState(false);
		navigate(HomeEnum.HOME_PAGE);
	};

	const shouldResetUnitResponseState = [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
	].includes(activeBookingFlowStep);

	const handleNext = () => {
		if (activeBookingFlowStep === stepLabels.length) {
			return;
		}
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});
		if (shouldResetUnitResponseState) {
			resetUnitResponseState();
		}
		if (isUsingGeolocation && activeBookingFlowStep < UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				hasGeoDistrictsFilter: false,
			}));
		}

		setActiveBookingFlowStep(value => value + 1);
	};

	const customFooterMarginBottom = () => {
		if (isUsingGeolocation) return undefined;
		if (activeBookingFlowStep === DISTRICT_STEP && isMobile) {
			return '142px';
		}

		return undefined;
	};

	useEffect(() => {
		const isFirstVisitValue = !stepsVisited.current.has(activeBookingFlowStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeBookingFlowStep);
	}, [activeBookingFlowStep]);

	useEffect(() => {
		setHideSearchBar(false);
		setHideStepBar(false);
		clearSearch();
	}, [activeBookingFlowStep]);

	return {
		getHeaderTitle,
		handleBack,
		handleClose,
		shouldHideBackBtn,
		isMobile,
		hideStepBar,
		activeBookingFlowStep,
		stepLabels,
		shouldHideSearchBar,
		searchBarTagId,
		searchBarPlaceholder,
		setActiveBookingFlowStep,
		handleConfirmExitFlow,
		abandonFlowState,
		setAbandonFlowState,
		isLastStep,
		showSwipeableDrawerBookingSummary,
		openDrawerState,
		setOpenDrawerState,
		handleChangeModals,
		handleNext,
		customFooterMarginBottom,
		shouldHideSearchBarAndBookingSummary,
		shouldUseNewResumeLayout,
		showOdontoSummarySectionGroupInGrid,
	};
}
